"use client";

import {Box, Button, FormControl, InputLabel, MenuItem, Select, Typography} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import {useRouter, useSearchParams} from "next/navigation";
import {
  DOCUMENTATION_QUERY_KEYS,
  DocumentationFilter,
  extractAvailableAuthorities,
  extractAvailableCodes, extractAvailableContentTypes,
  OriginCode,
  parseDocumentationFilter
} from "./document.filters";
import {authorityLabel, contentLabel, originCodeLabel} from "./document.labels";
import {AuthorityType, ContentType, Document} from "./document.model";

type Props = {
  documents: Document[];
};

export function DocumentationFilterBar({documents}: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const {origin, authority, content} = parseDocumentationFilter(searchParams);
  const codes = extractAvailableCodes(documents);
  const authorities = extractAvailableAuthorities(documents);
  const contentTypes = extractAvailableContentTypes(documents);

  const currentOrigin = origin ?? "ALL";
  const currentAuthority = authority ?? "ALL";
  const currentContent = content ?? "ALL";

  const isFiltered = !!origin || !!authority || !!content;

  function update(next: Partial<DocumentationFilter>) {
    const params = new URLSearchParams(searchParams.toString());

    if (next.origin === undefined) {
      params.delete(DOCUMENTATION_QUERY_KEYS.origin);
    } else {
      params.set(DOCUMENTATION_QUERY_KEYS.origin, next.origin);
    }

    if (next.authority === undefined) {
      params.delete(DOCUMENTATION_QUERY_KEYS.authority);
    } else {
      params.set(DOCUMENTATION_QUERY_KEYS.authority, next.authority);
    }

    if (next.content === undefined) {
      params.delete(DOCUMENTATION_QUERY_KEYS.content);
    } else {
      params.set(DOCUMENTATION_QUERY_KEYS.content, next.content);
    }

    router.replace(`/documentation?${params.toString()}`);
  }

  return (
      <>
        <Typography
            variant="overline"
            sx={{display: "block", mb: 1.5}}
        >
          Dokumente filtern
        </Typography>

        <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              alignItems: "center"
            }}
        >

          {/* Herkunft */}
          <FormControl size="small" sx={{minWidth: 180}}>
            <InputLabel id="origin-label">
              Herkunft
            </InputLabel>

            <Select
                labelId="origin-label"
                label="Herkunft"
                value={currentOrigin}
                onChange={(e) =>
                    update({
                      origin: e.target.value
                          ? (e.target.value as OriginCode)
                          : undefined,
                      authority
                    })
                }
            >
              <MenuItem value="ALL">
                Alle
              </MenuItem>

              {codes.map(c => (
                  <MenuItem key={c} value={c}>
                    {originCodeLabel(c)}
                  </MenuItem>
              ))}

            </Select>
          </FormControl>


          {/* Art der Stelle */}
          {/*<FormControl size="small" sx={{minWidth: 200}}>
            <InputLabel id="authority-label">
              Art der Stelle
            </InputLabel>

            <Select
                labelId="authority-label"
                label="Art der Stelle"
                value={currentAuthority}
                onChange={(e) =>
                    update({
                      origin: origin,
                      authority: e.target.value
                          ? (e.target.value as AuthorityType)
                          : undefined
                    })
                }
            >
              <MenuItem value="ALL">
                Alle
              </MenuItem>

              {authorities.map(a => (
                  <MenuItem key={a} value={a}>
                    {authorityLabel(a)}
                  </MenuItem>
              ))}

            </Select>
          </FormControl>*/}


          {/* Inhalt */}
          <FormControl size="small" sx={{minWidth: 200}}>
            <InputLabel id="content-label">
              Inhalt
            </InputLabel>

            <Select
              labelId="content-label"
              label="Inhalt"
              value={currentContent}
              onChange={(e) =>
                update({
                  origin,
                  authority,
                  content: e.target.value === "ALL"
                    ? undefined
                    : (e.target.value as ContentType)
                })
              }
            >
              <MenuItem value="ALL">
                Alle
              </MenuItem>

              {contentTypes.map(ct => (
                <MenuItem key={ct} value={ct}>
                  {contentLabel(ct)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          {isFiltered && (
              <Button
                  startIcon={<ClearIcon/>}
                  variant="text"
                  onClick={() => update({})}
              >
                Filter zurücksetzen
              </Button>
          )}

        </Box>
      </>
  );
}
